import { useEffect, useState } from "react";
import { Zap, Radio } from "lucide-react";

const ROBOT_A = "/robots/robot_a.png";
const ROBOT_B = "/robots/robot_b.png";

const TypingDots = () => (
  <span className="absolute inset-0 flex items-center gap-1 px-4">
    {[0, 1, 2].map((i) => (
      <span key={i} className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
    ))}
  </span>
);

export const ChatSimulation = ({ title, msgA, msgB, match, online, startDelay = 0, testId = "chat-simulation-box" }) => {
  const [step, setStep] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    setStep(0);
    const d = cycle === 0 ? startDelay : 0;
    const timers = [
      setTimeout(() => setStep(1), d + 800),
      setTimeout(() => setStep(2), d + 2200),
      setTimeout(() => setStep(3), d + 3400),
      setTimeout(() => setStep(4), d + 5000),
      setTimeout(() => setStep(5), d + 6300),
      setTimeout(() => setCycle((c) => c + 1), d + 13000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [title, cycle]);

  return (
    <div
      className="relative w-full max-w-md bg-[#0F0F1A]/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden"
      data-testid={testId}
    >
      <div className="absolute -top-20 -right-20 w-48 h-48 bg-[#B026FF] blur-[100px] opacity-20 pointer-events-none" />
      <div className="text-xs font-bold text-white/50 uppercase tracking-[0.2em] flex items-center justify-between mb-6">
        <span className="flex items-center gap-2">
          <Radio size={14} className="text-[#00F5FF]" />
          {title}
        </span>
        <span className="flex items-center gap-1.5 text-[#00FFAA]">
          <span className="w-2 h-2 rounded-full bg-[#00FFAA]" />
          {online}
        </span>
      </div>

      <div className="flex flex-col justify-start">
        <div className={`flex items-end gap-3 mb-4 transition-opacity duration-500 ${step >= 1 ? "opacity-100" : "opacity-0"}`}>
          <img src={ROBOT_A} alt="Robot avatar A" className="w-10 h-10 rounded-full bg-[#B026FF]/20 border border-[#B026FF]/40 shrink-0 object-cover" />
          <div className="relative bg-gradient-to-br from-[#B026FF]/20 to-[#B026FF]/5 border border-[#B026FF]/20 text-white/90 p-4 rounded-2xl rounded-bl-sm text-sm shadow-[0_4px_20px_rgba(176,38,255,0.15)] max-w-[85%]">
            <span className={step === 1 ? "invisible" : ""}>{msgA}</span>
            {step === 1 && <TypingDots />}
          </div>
        </div>

        <div className={`flex items-end gap-3 mb-4 flex-row-reverse transition-opacity duration-500 ${step >= 3 ? "opacity-100" : "opacity-0"}`}>
          <img src={ROBOT_B} alt="Robot avatar B" className="w-10 h-10 rounded-full bg-[#00F5FF]/20 border border-[#00F5FF]/40 shrink-0 object-cover" />
          <div className="relative bg-gradient-to-br from-[#00F5FF]/20 to-[#00F5FF]/5 border border-[#00F5FF]/20 text-white/90 p-4 rounded-2xl rounded-br-sm text-sm shadow-[0_4px_20px_rgba(0,245,255,0.15)] max-w-[85%]">
            <span className={step === 3 ? "invisible" : ""}>{msgB}</span>
            {step === 3 && <TypingDots />}
          </div>
        </div>

        <button
          data-testid="match-banner"
          className={`mt-2 w-full p-4 rounded-xl bg-gradient-to-r from-[#00FFAA]/20 to-[#00F5FF]/20 border border-[#00FFAA]/50 text-center shadow-[0_0_25px_rgba(0,255,170,0.35)] cursor-pointer transition-opacity duration-500 ${step >= 5 ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        >
          <span className="text-[#00FFAA] font-bold text-sm tracking-widest uppercase flex items-center justify-center gap-2">
            <Zap size={16} className="fill-[#00FFAA]" />
            {match}
          </span>
        </button>
      </div>
    </div>
  );
};
